import React, { useState } from 'react'
import PropTypes from 'prop-types'
import { Button, DialogConfirm } from 'components'
import { toast } from 'react-toastify'
import {
  FormControl,
  FormControlLabel,
  FormLabel,
  Radio,
  RadioGroup,
  TextField,
} from '@mui/material'
import { cloneDeep } from 'lodash'
import AddAttachments from 'components/AddAttachments'

import { removeAttachment } from 'utils/attachmentOperations'
import { useSelector } from 'react-redux'
import { submitExtraAutoAnswer } from 'services/apiKYP'

import styles from './styles'

const initialValues = {
  answer_type: "financial_pendency",
  recognizes_the_restrictions: false,
  is_improper_billing: false,
  judicialised_the_situation: false,
  evidence_of_judicialization: [],
  expected_deadline_to_regularize: '',
  has_payment_plan: false,
  proof_of_discharge_negotiation: [],
}

const initialErrors = {
  evidence_of_judicialization: [],
  expected_deadline_to_regularize: false,
}

function FinancialPendingsForm(props) {
  const { questionData } = props

  const token = useSelector((state) => state.auth.access_token)
  const classes = styles()

  const [formState, setFormState] = useState(initialValues)
  const [formErrors, setFormErrors] = useState(initialErrors)
  const [loading, setLoading] = useState(false)
  const [open, setOpen] = useState(false)

  const handleValidate = () => {
    const errors = cloneDeep(initialErrors)
    Object.keys(errors).forEach((key) => {
      if (key === 'evidence_of_judicialization') {
        errors[key] =
          formState.evidence_of_judicialization.length === 0 &&
          formState.judicialised_the_situation
        return
      }
      if (key === 'expected_deadline_to_regularize') {
        errors[key] =
          formState.expected_deadline_to_regularize === '' &&
          formState.has_payment_plan
        return
      }
    })
    setFormErrors(errors)
    return Object.keys(errors).every((key) => errors[key] === false)
  }

  const cleanForm = () => {
    setFormState(initialValues)
    setFormErrors(initialErrors)
  }

  const handleSubmit = async () => {
    if (handleValidate()) {
      setLoading(true)
      submitExtraAutoAnswer(token, questionData.uuid, formState)
        .then(() => {
          toast.success('Resposta enviada com sucesso')
          cleanForm()
        })
        .catch(() => {
          toast.error('Erro ao enviar respostas, por favor tente novamente')
        })
        .finally(() => {
          setLoading(false)
        })
    } else {
      toast.error(
        'Há campos obrigatórios vazios. Verifique os campos preenchidos e tente novamente.',
      )
    }
  }

  const handleCleanAction = () => {
    setOpen(false)
    // create a new array with the join of the two arrays to delete the attachments
    const attachments = [
      ...cloneDeep(formState.evidence_of_judicialization),
      ...cloneDeep(formState.proof_of_discharge_negotiation),
    ]
    if (attachments.length > 0) {
      Promise.all(
        attachments.map((attachment) =>
          removeAttachment(attachment.uuid, token),
        ),
      )
    }
    setFormState(initialValues)
    setFormErrors(initialErrors)
  }

  const handleFieldChange = (event) => {
    const newFormState = cloneDeep(formState)
    const key = event.target.id
    newFormState[key] = event.target.value
    setFormState(newFormState)
  }

  const handleUpdateJudicializationEvidences = (attachmentsList) => {
    const newFormState = cloneDeep(formState)
    newFormState.evidence_of_judicialization = attachmentsList
    setFormState(newFormState)
  }

  const handleUpdateProofOfDischarge = (attachmentsList) => {
    const newFormState = cloneDeep(formState)
    newFormState.proof_of_discharge_negotiation = attachmentsList
    setFormState(newFormState)
  }

  const handleRadioChange = (event) => {
    const key = event.target.name
    const newFormState = cloneDeep(formState)
    newFormState[key] = event.target.value === 'yes'
    setFormState(newFormState)
  }
  
  const handleOpenConfirmCleaning = () => {
    setOpen(true)
  }
  
  const handleCloseConfirmCleaning = () => {
    setOpen(false)
  }

  return (
    <div className={classes.root}>
      <h2>Pendência Financeira</h2>
      <div className={classes.formWrapper}>
        <div className={classes.subject}>
          <p>
            <span>Data de vencimento: </span>
            {questionData.occurrence.expiration_date}
          </p>
          <p>
            <span>Data da inclusão: </span>
            {questionData.occurrence.inclusion_date}
          </p>
          <p>
            <span>Fonte: </span>
            {questionData.occurrence.informant}
          </p>
          <p>
            <span>Credor: </span>
            {questionData.occurrence.creditor}
          </p>
          <p>
            <span>Número do contrato: </span>
            {questionData.occurrence.contract || 'N/A'}
          </p>
          <p>
            <span>Valor: </span>
            {`${questionData.occurrence.currency} ${questionData.occurrence.value}`}
          </p>
        </div>
        <FormControl>
          <FormLabel>Você reconhece essa restrição?</FormLabel>
          <RadioGroup
            row
            value={formState.recognizes_the_restrictions ? 'yes' : 'no'}
            name="recognizes_the_restrictions"
            onChange={handleRadioChange}
          >
            <FormControlLabel value="yes" control={<Radio />} label="Sim" />
            <FormControlLabel value="no" control={<Radio />} label="Não" />
          </RadioGroup>
        </FormControl>
        {formState.recognizes_the_restrictions ? (
          <React.Fragment>
            <FormControl>
              <FormLabel>Esta é uma situação de cobrança indevida?</FormLabel>
              <RadioGroup
                row
                value={formState.is_improper_billing ? 'yes' : 'no'}
                name="is_improper_billing"
                onChange={handleRadioChange}
              >
                <FormControlLabel value="yes" control={<Radio />} label="Sim" />
                <FormControlLabel value="no" control={<Radio />} label="Não" />
              </RadioGroup>
            </FormControl>
            {formState.is_improper_billing ? (
              <React.Fragment>
                <FormControl>
                  <FormLabel>
                    Em caso de cobrança indevida, judicializou a situação?
                  </FormLabel>
                  <RadioGroup
                    row
                    value={formState.judicialised_the_situation ? 'yes' : 'no'}
                    name="judicialised_the_situation"
                    onChange={handleRadioChange}
                  >
                    <FormControlLabel
                      value="yes"
                      control={<Radio />}
                      label="Sim"
                    />
                    <FormControlLabel
                      value="no"
                      control={<Radio />}
                      label="Não"
                    />
                  </RadioGroup>
                </FormControl>
                {formState.judicialised_the_situation ? (
                  <AddAttachments
                    attachments={formState.evidence_of_judicialization}
                    updateAttachments={handleUpdateJudicializationEvidences}
                    error={formErrors.evidence_of_judicialization}
                    title="Caso tenha judicializado a situação, encaminhar evidências"
                  />
                ) : null}
              </React.Fragment>
            ) : (
              <React.Fragment>
                <FormControl>
                  <FormLabel>Há algum plano de pagamento?</FormLabel>
                  <RadioGroup
                    row
                    value={formState.has_payment_plan ? 'yes' : 'no'}
                    name="has_payment_plan"
                    onChange={handleRadioChange}
                  >
                    <FormControlLabel
                      value="yes"
                      control={<Radio />}
                      label="Sim"
                    />
                    <FormControlLabel
                      value="no"
                      control={<Radio />}
                      label="Não"
                    />
                  </RadioGroup>
                </FormControl>
                {formState.has_payment_plan ? (
                  <React.Fragment>
                    <TextField
                      id="expected_deadline_to_regularize"
                      label="Expectativa de prazo para regularização"
                      onChange={handleFieldChange}
                      type="date"
                      fullWidth
                      value={formState.expected_deadline_to_regularize}
                      error={formErrors.expected_deadline_to_regularize}
                      InputLabelProps={{ shrink: true }}
                      size="small"
                    />
                    <AddAttachments
                      attachments={formState.proof_of_discharge_negotiation}
                      updateAttachments={handleUpdateProofOfDischarge}
                      title="Eventuais comprovantes de quitação/negociação para análise"
                    />
                  </React.Fragment>
                ) : null}
              </React.Fragment>
            )}
          </React.Fragment>
        ) : null}
        <div className={classes.buttonsContainer}>
          <Button onClick={handleOpenConfirmCleaning} variant="text">
            Limpar formulário
          </Button>
          <Button onClick={handleSubmit} loading={loading}>
            Responder
          </Button>
        </div>
      </div>
      <DialogConfirm 
        title='Tem certeza de que deseja limpar este formulário?'
        onConfirm={handleCleanAction}
        onCancel={handleCloseConfirmCleaning}
        open={open}
      >
        Esta ação é irreversível e não poderá ser desfeita.
      </DialogConfirm>
    </div>
  )
}

FinancialPendingsForm.propTypes = {
  questionData: PropTypes.shape({
    uuid: PropTypes.string.isRequired,
    text: PropTypes.string,
    occurrence: PropTypes.object,
    answered: PropTypes.bool.isRequired,
    subject: PropTypes.string.isRequired,
    answer: PropTypes.object,
  }).isRequired,
}

export default FinancialPendingsForm
